import React from 'react';
import { FILLED, OUTLINED, VIEW_BOX, IconName } from './paths';

interface IconProps {
  name: IconName;
  /** Use the filled variant of the glyph. */
  filled?: boolean;
  className?: string;
  /** Inline style, for the odd icon that is transformed or recoloured. */
  style?: React.CSSProperties;
  /** Accessible name. Omit for decorative icons (they get aria-hidden). */
  title?: string;
}

/**
 * Inline SVG icon, standing in for the Material Symbols icon font.
 *
 * Sized in `em` with `width:1em;height:1em`, so the same text-* class that
 * sized the old font glyph still sizes this, and colour comes from
 * currentColor. That keeps the swap from changing any layout.
 *
 * Inlined on purpose: Google serves Material Symbols as a single 4 MB woff2
 * for the whole icon set, and this site draws 18 glyphs from it. An inline
 * path also cannot render as a blank box the way an unloaded icon font does.
 */
const Icon: React.FC<IconProps> = ({ name, filled = false, className = '', style, title }) => {
  const paths = (filled ? FILLED[name] : OUTLINED[name]) ?? OUTLINED[name] ?? FILLED[name];
  if (!paths) return null;

  return (
    <svg
      className={`icon ${className}`.trim()}
      style={style}
      viewBox={VIEW_BOX}
      width="1em"
      height="1em"
      fill="currentColor"
      focusable="false"
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
    >
      {title ? <title>{title}</title> : null}
      {paths.map((d, i) => (
        <path key={i} d={d} />
      ))}
    </svg>
  );
};

export default Icon;
